/* eslint-disable react/jsx-props-no-spreading */
import Error from "next/error";
import Head from "next/head";
import Script from "next/script";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { CookiesProvider } from "react-cookie";
import useRollbar from "hooks/useRollbar";
import CookiesNoticeAdapter from "modules/common/components/CookiesNoticeAdapter";
import { LocalizationContextProvider } from "contexts/LocalizationContext";
import useGTM from "hooks/useGTM";
import useMixpanel from "hooks/useMixpanel";
import { SettingsContextProvider } from "contexts/SettingsContext";
import { Chakra, ChakraTheme, Fonts } from "@airhelp/react-ui-components";
import useUserTrackingCookie from "hooks/useUserTrackingCookie";
import config from "config/config";
import showAntiFlickerScript from "utils/serverSideUtils/datasources/showAntiFlickerScript";
import { useExtendedRouter } from "hooks";

const { ChakraProvider } = Chakra;
function App({ Component, pageProps }) {
  const {
    translations,
    language = "en",
    pageType,
    errorCode = null,
    isTrackingless = false,
    settings = {},
    antiFlickerSettings,
  } = pageProps;

  const antiFlickerScript = `
    (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
    h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
    (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
    })(window,document.documentElement,'async-hide','dataLayer',4000,
    {'${config.gtmCode}':true});
  `;

  const { asPath: pagePath } = useExtendedRouter();
  const showAntiFlicker = showAntiFlickerScript(antiFlickerSettings, pagePath);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useMixpanel(language, pageType);
  useGTM(language, pageType, isTrackingless);
  useUserTrackingCookie();
  useRollbar();

  if (errorCode) {
    return <Error statusCode={errorCode} />;
  }

  return (
    <>
      <Head>
        {showAntiFlicker && (
          <>
            <Script
              key="optimize-anti-flicker"
              id="optimize-anti-flicker"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: antiFlickerScript }}
            />
            <style key="optimize-anti-flicker-styles">{`.async-hide { opacity: 0 !important}`}</style>
          </>
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <CookiesProvider>
        <ChakraProvider theme={ChakraTheme}>
          <Fonts />
          <SettingsContextProvider settings={settings}>
            <LocalizationContextProvider language={language} translations={translations}>
              <Component {...pageProps} />
              <CookiesNoticeAdapter />
            </LocalizationContextProvider>
          </SettingsContextProvider>
        </ChakraProvider>
      </CookiesProvider>
    </>
  );
}

export default App;

App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.shape({
    language: PropTypes.string,
    pageType: PropTypes.string,
    errorCode: PropTypes.number,
    isTrackingless: PropTypes.bool,
    translations: PropTypes.objectOf(PropTypes.string),
    settings: PropTypes.shape({}),
    antiFlickerSettings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }).isRequired,
  router: PropTypes.shape({ query: PropTypes.shape({ language: PropTypes.string }) }).isRequired,
};
